// ---------------------------------------------------------------------------
// Section 1: Content — text, variant, placeholder, icon, visible
// ---------------------------------------------------------------------------

import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { InspectorSection } from '@/components/inspector'
import {
  InlineTextField,
  InlineSelectField,
  VARIANT_OPTIONS,
  VARIANT_OPTIONS_BY_TYPE,
  SIZE_OPTIONS_BY_TYPE,
  VARIANT_TYPES,
  SIZE_TYPES,
} from '../fields'
import type { CogoElement } from '@/features/page-editor/store/types'
/** Types that have placeholder */
const PLACEHOLDER_TYPES = new Set(['input', 'textarea', 'select'])
/** Types that use an icon */
const ICON_TYPES = new Set(['button', 'badge', 'icon'])

export function ContentSection({
  element,
  updateProp,
}: {
  element: CogoElement
  updateProp: (updates: Partial<CogoElement>) => void
}) {
  const showVariant = VARIANT_TYPES.has(element.type) || !!element.variant
  const showPlaceholder = PLACEHOLDER_TYPES.has(element.type) || !!element.placeholder
  const showIcon = ICON_TYPES.has(element.type) || !!element.icon

  return (
    <InspectorSection title="Content">
      {/* Text (editable) */}
      <InlineTextField
        label="Text"
        value={element.text ?? ''}
        onChange={(text) => updateProp({ text })}
        placeholder="Enter text..."
        show={element.text !== undefined}
      />

      {/* Variant — per-type options from CVA definitions */}
      <InlineSelectField
        label="Variant"
        value={element.variant ?? ''}
        onChange={(variant) => updateProp({ variant })}
        options={VARIANT_OPTIONS_BY_TYPE[element.type] ?? VARIANT_OPTIONS}
        show={showVariant}
      />

      {/* Size — only for types with CVA size variants */}
      <InlineSelectField
        label="Size"
        value={element.size ?? ''}
        onChange={(size) => updateProp({ size })}
        options={SIZE_OPTIONS_BY_TYPE[element.type] ?? []}
        show={SIZE_TYPES.has(element.type)}
      />

      {/* Placeholder — only for input/textarea/select */}
      <InlineTextField
        label="Placeholder"
        value={element.placeholder ?? ''}
        onChange={(placeholder) => updateProp({ placeholder })}
        placeholder="Placeholder text..."
        show={showPlaceholder}
      />

      {/* Icon — only for button/badge/icon types */}
      <InlineTextField
        label="Icon"
        value={element.icon ?? ''}
        onChange={(icon) => updateProp({ icon })}
        placeholder="e.g. lucide:check"
        show={showIcon}
      />

      {/* Visible — Eye icon toggle */}
      <div className="flex justify-between items-center gap-2">
        <span className="text-muted-foreground text-xs shrink-0">Visible</span>
        <button
          type="button"
          className={cn(
            'inline-flex items-center justify-center rounded-xl p-1.5 transition-colors',
            (element.visible !== false && element.visible !== 'false')
              ? 'text-foreground hover:bg-muted'
              : 'text-muted-foreground/40 hover:bg-muted',
          )}
          onClick={() => {
            const isVisible = element.visible !== false && element.visible !== 'false'
            updateProp({ visible: !isVisible })
          }}
          title={(element.visible !== false && element.visible !== 'false') ? 'Hide element' : 'Show element'}
        >
          {(element.visible !== false && element.visible !== 'false')
            ? <Eye className="h-4 w-4" />
            : <EyeOff className="h-4 w-4" />
          }
        </button>
      </div>
    </InspectorSection>
  )
}
